import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { playNotificationSound } from "../../services/notificationSound";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
}));

const NotificationsPopOver = ({ hideButton = false }) => {
	const classes = useStyles();
	const canUseDesktopNotifications =
		typeof window !== "undefined" &&
		"Notification" in window &&
		window.isSecureContext;

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const userQueueIds = useMemo(
		() =>
			Array.isArray(user?.queues)
				? user.queues.map(queue => Number(queue.id))
				: [],
		[user?.queues]
	);
	const userQueueIdsRef = useRef(userQueueIds);
	const pathnameRef = useRef(history.location.pathname);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const recentMessageIdsRef = useRef(new Set());
	const recentTransferIdsRef = useRef(new Set());
	const recentInternalTicketNotificationsRef = useRef(new Set());
	const previousUnreadByTicketRef = useRef(new Map());
	const knownTicketMetaRef = useRef(new Map());

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });

	const historyRef = useRef(history);

	const playTicketSound = useCallback(() => {
		void playNotificationSound("ticket");
	}, []);

	const playMessageSound = useCallback(() => {
		void playNotificationSound("message");
	}, []);

	const isNotifiableTicket = ticket =>
		["pending", "open"].includes(ticket.status) && Number(ticket.unreadMessages || 0) > 0;
	const isRealtimeNotifiableTicket = ticket =>
		["pending", "open"].includes(ticket.status);

	const isTruthy = value =>
		value === true ||
		value === "true" ||
		value === 1 ||
		value === "1";

	const isOutboundMessage = message =>
		isTruthy(message?.fromMe) ||
		(message?.userId && Number(message.userId) === Number(user?.id));

	const isOutboundMessageMeta = meta =>
		isTruthy(meta?.messageFromMe) ||
		(meta?.messageUserId && Number(meta.messageUserId) === Number(user?.id));

	const shouldSuppressMessageNotification = (message, meta) =>
		isTruthy(meta?.suppressNotification) ||
		String(message?.mediaType || "").toLowerCase() === "ticket_event";

	const getUnreadNotifications = ticketsList =>
		ticketsList.filter(ticket => isNotifiableTicket(ticket));

	const getActiveConversationTicketId = () => {
		const currentPathname =
			typeof window !== "undefined" && window.location?.pathname
				? window.location.pathname
				: pathnameRef.current;

		const match = currentPathname.match(/^\/tickets\/(\d+)/);
		return match ? Number(match[1]) : null;
	};

	const isViewingTicketConversation = ticketId => {
		const currentPathname =
			typeof window !== "undefined" && window.location?.pathname
				? window.location.pathname
				: pathnameRef.current;
		const activeTicketId = getActiveConversationTicketId();

		return (
			currentPathname.startsWith("/tickets") &&
			activeTicketId !== null &&
			Number(activeTicketId) === Number(ticketId) &&
			document.visibilityState === "visible"
		);
	};

	useEffect(() => {
		if (canUseDesktopNotifications && Notification.permission === "default") {
			Notification.requestPermission();
		}
	}, [canUseDesktopNotifications]);

	useEffect(() => {
		setNotifications(getUnreadNotifications(tickets));

		const unreadMap = new Map();
		tickets.forEach(ticket => {
			unreadMap.set(ticket.id, Number(ticket.unreadMessages || 0));
		});
		previousUnreadByTicketRef.current = unreadMap;

		const knownTicketMeta = new Map();
		tickets.forEach(ticket => {
			knownTicketMeta.set(Number(ticket.id), {
				createdAt: ticket.createdAt,
			});
		});
		knownTicketMetaRef.current = knownTicketMeta;
	}, [tickets]);

	useEffect(() => {
		pathnameRef.current = history.location.pathname;
	}, [history.location.pathname]);

	useEffect(() => {
		userQueueIdsRef.current = userQueueIds;
	}, [userQueueIds]);

	useEffect(() => {
		const socket = openSocket();

		const rememberMessageKey = messageKey => {
			if (!messageKey) {
				return false;
			}

			if (recentMessageIdsRef.current.has(messageKey)) {
				return true;
			}

			recentMessageIdsRef.current.add(messageKey);
			setTimeout(() => {
				recentMessageIdsRef.current.delete(messageKey);
			}, 5000);

			return false;
		};

		const rememberInternalTicketNotification = notificationKey => {
			if (!notificationKey) {
				return false;
			}

			if (recentInternalTicketNotificationsRef.current.has(notificationKey)) {
				return true;
			}

			recentInternalTicketNotificationsRef.current.add(notificationKey);
			setTimeout(() => {
				recentInternalTicketNotificationsRef.current.delete(notificationKey);
			}, 5000);

			return false;
		};

		const notifyRealtimeTicket = ({
			ticket,
			messageId,
			preview,
			usePrioritySound = false
		}) => {
			if (!ticket || !isRealtimeNotifiableTicket(ticket)) {
				return;
			}

			if (ticket.userId && Number(ticket.userId) !== Number(user?.id)) {
				return;
			}

			const dedupeKey =
				messageId || `ticket:${ticket.id}:${ticket.updatedAt || ticket.updated_at || ""}`;

			if (rememberMessageKey(dedupeKey)) {
				return;
			}

			if (isViewingTicketConversation(ticket.id)) {
				return;
			}

			handleNotifications({
				message: {
					id: dedupeKey,
					body: preview || ticket.lastMessage || "Nova mensagem",
					read: false,
				},
				contact: ticket.contact,
				ticket,
			}, usePrioritySound);
		};

		const notifyNewPendingTicket = ({ ticket, messageId }) => {
			if (!ticket || ticket.status !== "pending" || ticket.userId) {
				return;
			}

			const dedupeKey =
				messageId || `new-pending-ticket:${ticket.id}:${ticket.updatedAt || ""}`;

			if (rememberMessageKey(dedupeKey)) {
				return;
			}

			playTicketSound();

			if (!canUseDesktopNotifications || Notification.permission !== "granted") {
				return;
			}

			const notification = new Notification("Novo atendimento aguardando", {
				body: `${ticket.contact?.name || ticket.contact?.number || "Atendimento"} - ${format(new Date(), "HH:mm")}`,
				icon: ticket.contact?.profilePicUrl,
				tag: `new-pending:${ticket.id}`,
				renotify: true,
			});

			notification.onclick = e => {
				e.preventDefault();
				window.focus();
				historyRef.current.push(`/tickets/${ticket.id}`);
			};

			setDesktopNotifications(prevState => {
				const notificationIndex = prevState.findIndex(
					item => item.tag === notification.tag
				);
				if (notificationIndex !== -1) {
					prevState[notificationIndex] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});
		};

		socket.on("connect", () => {
			socket.emit("joinNotification");
			socket.emit("joinTickets", "pending");
			socket.emit("joinTickets", "open");
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}

			if (data.action === "update" && data.ticket) {
				const normalizedTicketId = Number(data.ticket.id);
				const previousUnread =
					previousUnreadByTicketRef.current.get(normalizedTicketId) || 0;
				const currentUnread = Number(data.ticket.unreadMessages || 0);
				previousUnreadByTicketRef.current.set(normalizedTicketId, currentUnread);

				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);

					if (!isNotifiableTicket(data.ticket)) {
						if (ticketIndex !== -1) {
							prevState.splice(ticketIndex, 1);
							return [...prevState];
						}

						return prevState;
					}

					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}

					return [data.ticket, ...prevState];
				});

				if (
					data.meta?.source === "message" &&
					data.meta?.messageId &&
					!isOutboundMessageMeta(data.meta) &&
					!shouldSuppressMessageNotification(null, data.meta)
				) {
					const knownTicketMeta =
						knownTicketMetaRef.current.get(normalizedTicketId) || {};
					const createdAt = data.ticket.createdAt || knownTicketMeta.createdAt;

					knownTicketMetaRef.current.set(normalizedTicketId, {
						createdAt: createdAt || null,
					});

					if (isTruthy(data.meta?.ticketNewlyCreated)) {
						notifyNewPendingTicket({
							ticket: data.ticket,
							messageId: data.meta?.messageId,
						});
					} else {
						notifyRealtimeTicket({
							ticket: data.ticket,
							messageId: data.meta?.messageId,
							preview: data.meta?.preview,
						});
					}
				}



                // Movements such as transfer or PENDING > OPEN acceptance should only
                // update lists; notification sound is reserved for new pending tickets
                // and inbound customer messages handled above.
			}
		});

        socket.on("ticketTransfer", () => {
            // Transfer and acceptance events only refresh ticket lists.
            // Notification sound is intentionally limited to new pending tickets
            // and inbound customer messages.
        });


		socket.on("appMessage", data => {
			if (
				data.action === "create" &&
				!isOutboundMessage(data.message) &&
				!shouldSuppressMessageNotification(data.message, data.meta)
			) {
				if (!isRealtimeNotifiableTicket(data.ticket)) {
					return;
				}

				const normalizedTicket = {
					...data.ticket,
					unreadMessages: Math.max(Number(data.ticket?.unreadMessages || 0), 1),
				};
				previousUnreadByTicketRef.current.set(
					normalizedTicket.id,
					Number(normalizedTicket.unreadMessages || 0)
				);
				knownTicketMetaRef.current.set(Number(normalizedTicket.id), {
					createdAt: normalizedTicket.createdAt || null,
				});

				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === normalizedTicket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = normalizedTicket;
						return [...prevState];
					}
					return [normalizedTicket, ...prevState];
				});

				if (isTruthy(data.meta?.ticketNewlyCreated)) {
					notifyNewPendingTicket({
						ticket: normalizedTicket,
						messageId: data.message.id,
					});
				} else {
					notifyRealtimeTicket({
						ticket: normalizedTicket,
						messageId: data.message.id,
						preview: data.message.body,
					});
				}
			}
		});

		socket.on("internalTicketNotification", data => {
			const ticket = data?.ticket;
			const message =
				String(data?.message || "").trim() ||
				`Atualização no ticket interno ${ticket?.protocol || "#"}.`;

			if (!ticket?.id) {
				return;
			}

			const notificationKey = `${ticket.id}:${data?.action || "update"}:${
				ticket.updatedAt || ""
			}:${message}`;

			if (rememberInternalTicketNotification(notificationKey)) {
				return;
			}

			playTicketSound();

			toast.info(message);

			if (!canUseDesktopNotifications || Notification.permission !== "granted") {
				return;
			}

			const notification = new Notification("Ticket interno atualizado", {
				body: message,
				tag: `internal-ticket:${ticket.id}`,
				renotify: true,
			});

			notification.onclick = e => {
				e.preventDefault();
				window.focus();
				historyRef.current.push("/internal-tickets");
			};

			setDesktopNotifications(prevState => {
				const notificationIndex = prevState.findIndex(
					item => item.tag === notification.tag
				);
				if (notificationIndex !== -1) {
					prevState[notificationIndex] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});
		});

		return () => {
			socket.disconnect();
		};
	}, [canUseDesktopNotifications, user?.id, playMessageSound, playTicketSound]);

	const handleNotifications = (data, usePrioritySound = false) => {
		if (usePrioritySound) {
			playTicketSound();
		} else {
			playMessageSound();
		}

		if (!canUseDesktopNotifications || Notification.permission !== "granted") {
			return;
		}

		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	if (hideButton) {
		return null;
	}

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
